import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Course {
  id: string;
  title: string;
  description: string;
  instructor_name: string;
  student_count: number;
  created_at: string;
  status: string;
  start_date?: string;
  end_date?: string;
  schedule?: string;
}

interface Assignment {
  id: string;
  title: string;
  description: string;
  due_date: string;
  course_id: string;
  max_score: number;
  created_at: string;
}

interface MyCoursesProps {
  userProfile: any;
  onSwitchToAssignments?: () => void;
}

export default function MyCourses({ userProfile, onSwitchToAssignments }: MyCoursesProps) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showCourseDetailModal, setShowCourseDetailModal] = useState(false);
  const [courseStudents, setCourseStudents] = useState<any[]>([]);

  // Form state for creating assignment
  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    description: '',
    due_date: '',
    max_score: 100
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError('');
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('Current user:', user);
      
      // Sử dụng teacher_id thay vì instructor_name
      const teacherId = user.id;
      
      if (!teacherId) {
        throw new Error('Không tìm thấy thông tin giáo viên');
      }

      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-courses?teacher_id=${encodeURIComponent(teacherId)}`;
      console.log('Fetching from URL:', url);
      console.log('Teacher ID:', teacherId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`Không thể tải danh sách khóa học (${response.status})`);
      }

      const data = await response.json();
      console.log('Response data:', data);
      console.log('Number of courses received:', Array.isArray(data) ? data.length : 0);
      
      // Kiểm tra nếu có lỗi trong response
      if (data.error) {
        throw new Error(data.error);
      }

      // API trả về mảng trực tiếp
      const coursesArray = Array.isArray(data) ? data : [];
      console.log('Setting courses:', coursesArray);
      setCourses(coursesArray);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError(error instanceof Error ? error.message : 'Không thể tải danh sách khóa học');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseStudents = async (courseId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-enrollments?course_id=${courseId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Course students data:', data);
        // Đảm bảo data là mảng
        setCourseStudents(Array.isArray(data) ? data : []);
      } else {
        console.error('Failed to fetch course students:', response.status);
        setCourseStudents([]);
      }
    } catch (err) {
      console.error('Error fetching course students:', err);
      setCourseStudents([]);
    }
  };

  const handleCreateAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourse || !userProfile) return;

    try {
      console.log('=== BẮT ĐẦU TẠO BÀI TẬP ===');
      console.log('Selected course:', selectedCourse);
      console.log('User profile:', userProfile);
      
      // Kiểm tra và validate dữ liệu
      if (!assignmentForm.title.trim()) {
        alert('❌ Vui lòng nhập tiêu đề bài tập');
        return;
      }
      
      if (!assignmentForm.description.trim()) {
        alert('❌ Vui lòng nhập mô tả bài tập');
        return;
      }
      
      if (!assignmentForm.due_date) {
        alert('❌ Vui lòng chọn hạn nộp bài');
        return;
      }

      const assignmentData = {
        title: assignmentForm.title.trim(),
        description: assignmentForm.description.trim(),
        due_date: new Date(assignmentForm.due_date).toISOString(),
        course_id: selectedCourse.id,
        instructor_id: userProfile.id,
        max_score: parseInt(assignmentForm.max_score.toString()) || 100
      };
      
      console.log('📤 Dữ liệu gửi đi:', JSON.stringify(assignmentData, null, 2));

      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-assignment`;
      console.log('🌐 URL:', url);

      // Sử dụng Edge Function để tạo bài tập (bypass RLS)
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assignmentData)
      }).catch((fetchError: Error) => {
        console.error('❌ Fetch error:', fetchError);
        throw new Error(`Lỗi kết nối: ${fetchError.message}`);
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response ok:', response.ok);
      console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));

      // Đọc response text trước để debug
      const responseText = await response.text();
      console.log('📥 Response text:', responseText);

      // Parse JSON
      let result;
      try {
        result = responseText ? JSON.parse(responseText) : {};
        console.log('📥 Parsed result:', JSON.stringify(result, null, 2));
      } catch (parseError) {
        console.error('❌ JSON parse error:', parseError);
        console.error('Response text was:', responseText);
        alert(`❌ Lỗi khi xử lý phản hồi từ server:\n\nStatus: ${response.status}\nResponse: ${responseText.substring(0, 300)}`);
        return;
      }

      // Kiểm tra lỗi
      if (!response.ok) {
        console.error('❌ Edge function error:', result);
        
        let errorMsg = 'Không thể tạo bài tập';
        
        if (result.error) {
          errorMsg = result.error;
        }
        
        if (result.details) {
          errorMsg += `\n\nChi tiết: ${result.details}`;
        }
        
        if (result.code) {
          errorMsg += `\n\nMã lỗi: ${result.code}`;
        }
        
        if (result.hint) {
          errorMsg += `\n\nGợi ý: ${result.hint}`;
        }
        
        alert(`❌ ${errorMsg}`);
        return;
      }

      if (result.error) {
        console.error('❌ Result contains error:', result.error);
        alert(`❌ ${result.error}`);
        return;
      }

      // Kiểm tra nếu không có dữ liệu trả về
      if (!result || (!result.success && !result.assignment)) {
        console.error('❌ Empty or invalid result from server');
        alert(`❌ Server không trả về dữ liệu hợp lệ.\n\nResponse: ${JSON.stringify(result)}`);
        return;
      }

      console.log('✅ Assignment created successfully:', result);
      
      // Đóng modal và reset form
      setShowAssignmentModal(false);
      setAssignmentForm({
        title: '',
        description: '',
        due_date: '',
        max_score: 100
      });

      // Hiển thị thông báo thành công
      alert('✅ Đã tạo bài tập thành công! Đang chuyển sang trang Bài tập...');
      
      // Chuyển sang tab Bài tập sau 500ms
      if (onSwitchToAssignments) {
        setTimeout(() => {
          onSwitchToAssignments();
        }, 500);
      }

    } catch (err: unknown) {
      console.error('❌ Error creating assignment:', err);
      console.error('Error type:', typeof err);
      
      if (err instanceof Error) {
        console.error('Error name:', err.name);
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
        alert(`❌ Lỗi: ${err.message}\n\nVui lòng kiểm tra:\n1. Kết nối mạng\n2. Cấu hình Supabase\n3. Console để xem chi tiết`);
      } else if (err && typeof err === 'object') {
        console.error('Error object:', err);
        const errorStr = JSON.stringify(err, null, 2);
        alert(`❌ Lỗi không xác định.\n\nVui lòng kiểm tra Console để xem chi tiết.\n\nError: ${errorStr}`);
      } else {
        console.error('Unknown error type:', err);
        alert(`❌ Lỗi không xác định: ${String(err)}`);
      }
    }
  };

  const handleViewStudents = (course: Course) => {
    setSelectedCourse(course);
    fetchCourseStudents(course.id);
    setShowStudentsModal(true);
  };

  const handleAssignHomework = (course: Course) => {
    setSelectedCourse(course);
    setShowAssignmentModal(true);
  };

  const handleViewCourseDetail = (course: Course) => {
    setSelectedCourse(course);
    setShowCourseDetailModal(true);
  };

  const handleManageSchedule = (course: Course) => {
    setSelectedCourse(course);
    setShowCourseDetailModal(false);
    alert(`Chức năng quản lý lịch học cho khóa "${course.title}" đang được phát triển.`);
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'active':
        return { text: 'Đang mở', class: 'bg-green-100 text-green-800' };
      case 'draft':
        return { text: 'Bản nháp', class: 'bg-yellow-100 text-yellow-800' };
      case 'completed':
        return { text: 'Đã hoàn thành', class: 'bg-blue-100 text-blue-800' };
      default:
        return { text: 'Đã đóng', class: 'bg-gray-100 text-gray-800' };
    }
  };

  const getStatusInfo = (course: any) => {
    const now = new Date();
    const startDate = course.start_date ? new Date(course.start_date) : null;
    const endDate = course.end_date ? new Date(course.end_date) : null;

    // Nếu có ngày bắt đầu và ngày kết thúc
    if (startDate && endDate) {
      if (now < startDate) {
        return {
          text: 'Chưa bắt đầu',
          color: 'bg-gray-100 text-gray-800',
          icon: 'ri-time-line'
        };
      } else if (now >= startDate && now <= endDate) {
        return {
          text: 'Đang học',
          color: 'bg-green-100 text-green-800',
          icon: 'ri-play-line'
        };
      } else {
        return {
          text: 'Đã hoàn thành',
          color: 'bg-blue-100 text-blue-800',
          icon: 'ri-check-line'
        };
      }
    }

    // Fallback cho các khóa học chưa có ngày
    switch (course.status) {
      case 'active':
        return {
          text: 'Đang mở',
          color: 'bg-green-100 text-green-800',
          icon: 'ri-play-line'
        };
      case 'draft':
        return {
          text: 'Bản nháp',
          color: 'bg-yellow-100 text-yellow-800',
          icon: 'ri-draft-line'
        };
      case 'completed':
        return {
          text: 'Đã hoàn thành',
          color: 'bg-blue-100 text-blue-800',
          icon: 'ri-check-line'
        };
      default:
        return {
          text: 'Đã đóng',
          color: 'bg-gray-100 text-gray-800',
          icon: 'ri-close-line'
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Đang tải khóa học...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <i className="ri-error-warning-line mr-2"></i>
              {error}
            </div>
            <button
              onClick={fetchCourses}
              className="text-red-600 hover:text-red-800 underline text-sm"
            >
              Thử lại
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <i className="ri-book-line text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Không thể tải khóa học</h3>
          <p className="text-gray-500 mb-4">Vui lòng kiểm tra kết nối mạng và thử lại</p>
          <button
            onClick={fetchCourses}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <i className="ri-refresh-line mr-2"></i>
            Tải lại
          </button>
        </div>
      </div>
    );
  }

  console.log('Rendering with courses:', courses);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Khóa học của tôi</h2>
          <p className="text-sm text-gray-500 mt-1">Tổng cộng: {courses.length} khóa học</p>
        </div>
        <button
          onClick={fetchCourses}
          className="flex items-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-all whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          Làm mới
        </button>
      </div>

      {courses.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <i className="ri-book-line text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có khóa học nào</h3>
          <p className="text-gray-500">Admin sẽ phân công khóa học cho bạn</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => {
            const statusInfo = getStatusInfo(course);
            return (
              <div key={course.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{course.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                      {statusInfo.text}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{course.description}</p>
                  
                  {/* Thông tin thời gian và lịch học */}
                  {(course.start_date || course.end_date || course.schedule) && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
                      {course.start_date && course.end_date && (
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-calendar-line mr-2"></i>
                          {new Date(course.start_date).toLocaleDateString('vi-VN')} - {new Date(course.end_date).toLocaleDateString('vi-VN')}
                        </div>
                      )}
                      {course.schedule && (
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-time-line mr-2"></i>
                          {course.schedule}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center">
                      <i className="ri-user-line mr-1"></i>
                      {course.student_count} học sinh
                    </span>
                    <span className="flex items-center">
                      <i className="ri-calendar-line mr-1"></i>
                      {new Date(course.created_at).toLocaleDateString('vi-VN')}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => handleViewCourseDetail(course)}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-eye-line mr-2"></i>
                      Xem chi tiết
                    </button>
                    <button
                      onClick={() => handleViewStudents(course)}
                      className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-group-line mr-2"></i>
                      Quản lý lớp học
                    </button>
                    <button
                      onClick={() => handleAssignHomework(course)}
                      className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-file-add-line mr-2"></i>
                      Giao bài tập
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Course Detail Modal */}
      {showCourseDetailModal && selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Chi tiết khóa học
                </h2>
                <button
                  onClick={() => setShowCourseDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Course Title and Status */}
              <div>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-2xl font-bold text-gray-900">{selectedCourse.title}</h3>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusInfo(selectedCourse).color}`}>
                    {getStatusInfo(selectedCourse).text}
                  </span>
                </div>
                <p className="text-gray-600">{selectedCourse.description}</p>
              </div>

              {/* Course Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center text-blue-600 mb-2">
                    <i className="ri-user-line mr-2"></i>
                    <span className="text-sm font-medium">Số học sinh</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{selectedCourse.student_count}</p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center text-green-600 mb-2">
                    <i className="ri-calendar-check-line mr-2"></i>
                    <span className="text-sm font-medium">Ngày tạo</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(selectedCourse.created_at).toLocaleDateString('vi-VN')}
                  </p>
                </div>
              </div>

              {/* Schedule Information */}
              {(selectedCourse.start_date || selectedCourse.end_date || selectedCourse.schedule) && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <i className="ri-calendar-line mr-2"></i>
                    Thông tin lịch học
                  </h4>
                  {selectedCourse.start_date && selectedCourse.end_date && (
                    <div className="flex items-center text-gray-700">
                      <i className="ri-time-line mr-2 text-gray-400"></i>
                      <span className="text-sm">
                        Thời gian: {new Date(selectedCourse.start_date).toLocaleDateString('vi-VN')} - {new Date(selectedCourse.end_date).toLocaleDateString('vi-VN')}
                      </span>
                    </div>
                  )}
                  {selectedCourse.schedule && (
                    <div className="flex items-center text-gray-700">
                      <i className="ri-calendar-2-line mr-2 text-gray-400"></i>
                      <span className="text-sm">Lịch học: {selectedCourse.schedule}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-3 gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowCourseDetailModal(false);
                    handleViewStudents(selectedCourse);
                  }}
                  className="bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm whitespace-nowrap cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-group-line mr-2"></i>
                  Xem danh sách học sinh
                </button>
                <button
                  onClick={() => {
                    setShowCourseDetailModal(false);
                    handleAssignHomework(selectedCourse);
                  }}
                  className="bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm whitespace-nowrap cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-file-add-line mr-2"></i>
                  Giao bài tập
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Students Modal */}
      {showStudentsModal && selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Học sinh - {selectedCourse.title}
                </h2>
                <button
                  onClick={() => setShowStudentsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              {courseStudents.length === 0 ? (
                <div className="text-center py-8">
                  <i className="ri-user-line text-4xl text-gray-300 mb-3"></i>
                  <p className="text-gray-500">Chưa có học sinh nào đăng ký khóa học này</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Học sinh
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tiến độ
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ngày đăng ký
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Thao tác
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {courseStudents.map((student, index) => (
                        <tr key={`${student.student_id}_${index}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <i className="ri-user-line text-blue-600"></i>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {student.student_name || student.student_email || 'Học sinh'}
                                </div>
                                <div className="text-sm text-gray-500">{student.student_email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-3">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full transition-all"
                                  style={{ width: `${student.progress || 0}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600">{student.progress || 0}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(student.enrolled_at).toLocaleDateString('vi-VN')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button className="text-blue-600 hover:text-blue-900 mr-3">
                              <i className="ri-eye-line"></i>
                            </button>
                            <button className="text-green-600 hover:text-green-900">
                              <i className="ri-message-line"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Giao bài tập - {selectedCourse.title}
                </h2>
                <button
                  onClick={() => setShowAssignmentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleCreateAssignment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tiêu đề bài tập *
                  </label>
                  <input
                    type="text"
                    required
                    value={assignmentForm.title}
                    onChange={(e) => setAssignmentForm({...assignmentForm, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tiêu đề bài tập..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả bài tập *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={assignmentForm.description}
                    onChange={(e) => setAssignmentForm({...assignmentForm, description: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mô tả chi tiết về bài tập, yêu cầu, hướng dẫn..."
                    maxLength={500}
                  ></textarea>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hạn nộp *
                    </label>
                    <input
                      type="datetime-local"
                      required
                      value={assignmentForm.due_date}
                      onChange={(e) => setAssignmentForm({...assignmentForm, due_date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Điểm tối đa
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={assignmentForm.max_score}
                      onChange={(e) => setAssignmentForm({...assignmentForm, max_score: parseInt(e.target.value) || 100})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAssignmentModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line mr-2"></i>
                    Giao bài tập
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
